import { useState } from "react";
import { Plus, Edit2, Trash2, FileText } from "lucide-react";
import { TemplateFormDialog } from "./TemplateFormDialog";

interface Template {
  label: string;
  description?: string;
  slots: string[];
  layout: {
    hasSidebar: boolean;
  };
  recommended: string[];
}

interface Slot {
  label: string;
  description: string;
  position: string;
  allowsMultiple: boolean;
  responsive: {
    mobile: string;
    tablet: string;
    desktop: string;
  };
}

interface TemplatesTabProps {
  templates: Record<string, Template>;
  slots: Record<string, Slot>;
  sections: Record<string, any>;
  onSave: (updatedTemplates: Record<string, Template>) => Promise<void>;
  readOnly?: boolean;
}

export function TemplatesTab({
  templates,
  slots,
  sections,
  onSave,
  readOnly = false,
}: TemplatesTabProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(
    null,
  );
  const [isSaving, setIsSaving] = useState(false);

  const handleAdd = () => {
    setEditingTemplateId(null);
    setIsDialogOpen(true);
  };

  const handleEdit = (templateId: string) => {
    setEditingTemplateId(templateId);
    setIsDialogOpen(true);
  };

  const handleDelete = async (templateId: string) => {
    if (!confirm(`Eliminare il template "${templates[templateId]?.label}"?`)) {
      return;
    }

    setIsSaving(true);
    try {
      const updatedTemplates = { ...templates };
      delete updatedTemplates[templateId];
      await onSave(updatedTemplates);
    } catch (error) {
      console.error("Error deleting template:", error);
      alert("Errore durante l'eliminazione del template");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveTemplate = async (
    templateId: string,
    templateData: Template,
  ) => {
    setIsSaving(true);
    try {
      const updatedTemplates = {
        ...templates,
        [templateId]: templateData,
      };
      await onSave(updatedTemplates);
      setIsDialogOpen(false);
    } catch (error) {
      console.error("Error saving template:", error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const templateEntries = Object.entries(templates);

  return (
    <div className="templates-tab">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Templates</h2>
            <p className="text-sm text-gray-600 mt-1">
              Gestisci i template di pagina ({templateEntries.length} templates)
            </p>
          </div>

          {!readOnly && (
            <button
              onClick={handleAdd}
              className="btn-primary flex items-center space-x-2"
              disabled={isSaving}
            >
              <Plus className="w-4 h-4" />
              <span>Nuovo Template</span>
            </button>
          )}
        </div>
      </div>

      {/* Templates List */}
      <div className="px-6 py-6">
        {templateEntries.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Nessun template disponibile</p>
            {!readOnly && (
              <button
                onClick={handleAdd}
                className="btn-secondary mt-4 inline-flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>Crea il primo template</span>
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templateEntries.map(([templateId, template]) => (
              <div
                key={templateId}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 transition-colors"
              >
                {/* Card Header */}
                <div className="p-4 border-b border-gray-100">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">
                        {template.label}
                      </h3>
                      <p className="text-sm text-gray-500 font-mono">
                        {templateId}
                      </p>
                    </div>
                    {!readOnly && (
                      <div className="flex items-center space-x-1 ml-2">
                        <button
                          onClick={() => handleEdit(templateId)}
                          className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                          title="Modifica"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(templateId)}
                          disabled={isSaving}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Elimina"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  {template.description && (
                    <p className="text-sm text-gray-600 mt-2">
                      {template.description}
                    </p>
                  )}
                </div>

                {/* Card Body */}
                <div className="p-4 space-y-3">
                  {/* Slots */}
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase mb-2">
                      Slots ({template.slots.length})
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {template.slots.map((slotId) => (
                        <span
                          key={slotId}
                          className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded"
                          title={slots[slotId]?.label || slotId}
                        >
                          {slotId}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Layout */}
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase mb-1">
                      Layout
                    </p>
                    <p className="text-sm text-gray-700">
                      {template.layout.hasSidebar
                        ? "Con sidebar"
                        : "Senza sidebar"}
                    </p>
                  </div>

                  {/* Recommended Sections */}
                  {template.recommended && template.recommended.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase mb-2">
                        Raccomandati ({template.recommended.length})
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {template.recommended.slice(0, 3).map((sectionId) => (
                          <span
                            key={sectionId}
                            className="inline-flex items-center px-2 py-1 bg-green-100 text-green-700 text-xs rounded"
                          >
                            {sectionId}
                          </span>
                        ))}
                        {template.recommended.length > 3 && (
                          <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                            +{template.recommended.length - 3}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Dialog */}
      {isDialogOpen && (
        <TemplateFormDialog
          templateId={editingTemplateId}
          template={
            editingTemplateId ? templates[editingTemplateId] : undefined
          }
          availableSlots={slots}
          availableSections={Object.keys(sections)}
          existingTemplateIds={Object.keys(templates)}
          onSave={handleSaveTemplate}
          onCancel={() => setIsDialogOpen(false)}
          isSaving={isSaving}
        />
      )}
    </div>
  );
}
